import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Eye,
  Search,
  AlertTriangle,
  Mail,
  KeyRound,
  Download,
  Ban,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/use-auth";
import { SIGN_IN_PATH } from "@/lib/auth-routes";
import { toast } from "sonner";

interface ProfileRow {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
}

interface WorkspaceRow {
  id: string;
  name: string;
  plan: string | null;
  status: string | null;
  slug: string | null;
}

function getInitials(fullName: string | null, email: string) {
  if (fullName) {
    return fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email[0].toUpperCase();
}

async function logAuditAction(
  actorUserId: string,
  entityId: string | null,
  action: "create" | "update" | "delete",
  entityType: "contact" | "deal" | "property",
  metadata: { [key: string]: Json | undefined },
  workspaceId?: string,
) {
  await supabase.from("audit_logs").insert({
    action,
    entity_type: entityType,
    entity_id: entityId,
    actor_user_id: actorUserId,
    after_data: metadata,
    workspace_id: workspaceId ?? null,
  });
}

export default function AdminSupport() {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProfileRow[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Selected user + their workspaces
  const [selectedUser, setSelectedUser] = useState<ProfileRow | null>(null);
  const [userWorkspaces, setUserWorkspaces] = useState<WorkspaceRow[]>([]);

  // Action loading states
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Dialogs
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");

  // ─── Search users ────────────────────────────────────────────────────────────

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setIsSearching(true);
    setHasSearched(true);

    const { data, error } = await supabase
      .from("profiles")
      .select("id, user_id, email, full_name")
      .ilike("email", `%${q}%`);
    // .limit(10);

    setIsSearching(false);
    if (error) {
      toast.error("Failed to search users.");
      return;
    }
    const selfId = user?.id;
    const rows = (data ?? []).filter((p) => p.user_id !== selfId);
    setSearchResults(rows);
  }, [searchQuery, user?.id]);

  // ─── Fetch workspaces for a user ─────────────────────────────────────────────

  const fetchUserWorkspaces = async (userId: string) => {
    const { data: memberRows } = await supabase
      .from("workspace_members")
      .select("workspace_id")
      .eq("user_id", userId);

    if (!memberRows?.length) {
      setUserWorkspaces([]);
      return;
    }

    const ids = memberRows.map((r) => r.workspace_id);
    const { data: wsRows } = await supabase
      .from("workspaces")
      .select("id, name, plan, status, slug")
      .in("id", ids);

    setUserWorkspaces(wsRows ?? []);
  };

  // ─── Start impersonation ─────────────────────────────────────────────────────

  const handleStartImpersonation = async (profile: ProfileRow) => {
    setLoadingAction("impersonate");
    try {
      // Server-side: generate a magic-link token for the target user
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        "impersonate-user",
        { body: { target_email: profile.email } },
      );

      if (fnError || !fnData?.hashed_token) {
        toast.error(fnData?.error ?? "Failed to start impersonation session.");
        return;
      }

      // Switch the Supabase client session to the target user
      await startImpersonation(profile.email, fnData.hashed_token);

      // Navigate into the app as the impersonated user — banner will be visible
      navigate("/dashboard");
    } catch (err) {
      toast.error(
        (err as Error).message || "Failed to start impersonation session.",
      );
    } finally {
      setLoadingAction(null);
    }
  };

  // ─── Reset password ───────────────────────────────────────────────────────────

  const handleResetPassword = async () => {
    if (!selectedUser) {
      toast.warning("Select a user first using the search above.");
      return;
    }
    setLoadingAction("reset");
    const { error } = await supabase.auth.resetPasswordForEmail(
      selectedUser.email,
      { redirectTo: `${window.location.origin}${SIGN_IN_PATH}` },
    );

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`Password reset email sent to ${selectedUser.email}`);
    }
    setLoadingAction(null);
  };

  // ─── Resend verification ──────────────────────────────────────────────────────

  const handleResendVerification = async () => {
    if (!selectedUser) {
      toast.warning("Select a user first using the search above.");
      return;
    }
    setLoadingAction("verify");
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: selectedUser.email,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`Verification email resent to ${selectedUser.email}`);
    }
    setLoadingAction(null);
  };

  // ─── Export workspace data ────────────────────────────────────────────────────

  const handleExportData = async () => {
    if (!selectedWorkspaceId) return;

    setLoadingAction("export");
    setExportDialogOpen(false);

    const wsId = selectedWorkspaceId;

    const [
      { data: workspace },
      { data: contacts },
      { data: properties },
      { data: members },
    ] = await Promise.all([
      supabase.from("workspaces").select("*").eq("id", wsId).single(),
      supabase.from("contacts").select("*").eq("workspace_id", wsId),
      supabase.from("properties").select("*").eq("workspace_id", wsId),
      supabase.from("workspace_members").select("*").eq("workspace_id", wsId),
    ]);

    const exportPayload = {
      exported_at: new Date().toISOString(),
      workspace,
      contacts: contacts ?? [],
      properties: properties ?? [],
      members: members ?? [],
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workspace-export-${wsId}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("Workspace data exported.");
    setLoadingAction(null);
  };

  // ─── Suspend workspace ────────────────────────────────────────────────────────

  const handleSuspendWorkspace = async () => {
    if (!selectedWorkspaceId) return;

    setLoadingAction("suspend");
    setSuspendDialogOpen(false);

    const wsId = selectedWorkspaceId;
    // workspaces.status CHECK: active | suspended | cancelled (not inactive).
    const { error } = await supabase
      .from("workspaces")
      .update({ status: "suspended" })
      .eq("id", wsId);

    // workspace_members.status CHECK: active | invited | inactive (not suspended).
    const { error: memberError } = await supabase
      .from("workspace_members")
      .update({ status: "inactive" })
      .eq("workspace_id", wsId);

    if (error || memberError) {
      toast.error(error?.message ?? memberError?.message ?? "Update failed");
    } else {
      setUserWorkspaces((prev) =>
        prev.map((w) => (w.id === wsId ? { ...w, status: "suspended" } : w)),
      );
      toast.success("Workspace has been suspended.");
    }
    setLoadingAction(null);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const noUserWarning = !selectedUser;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Support Tools</h1>
        <p className="text-muted-foreground mt-1">
          Tools for troubleshooting and user support
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* View As User */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              View As User
            </CardTitle>
            <CardDescription>
              Impersonate a user to troubleshoot their issues
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search user by email..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <Button
                variant="secondary"
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
              >
                {isSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Search"
                )}
              </Button>
            </div>

            {/* Search results */}
            {hasSearched && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {searchResults.length === 0 && !isSearching ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No users found.
                  </p>
                ) : (
                  searchResults.map((profile) => {
                    const isSelected =
                      selectedUser?.user_id === profile.user_id;
                    return (
                      <div
                        key={profile.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                          isSelected
                            ? "border-primary/50 bg-primary/5"
                            : "border-border hover:bg-accent/50"
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedUser(null);
                            setUserWorkspaces([]);
                            setSelectedWorkspaceId("");
                            console.log("clearing selected user");
                          } else {
                            setSelectedUser(profile);
                            console.log(
                              "fetching workspaces for user",
                              profile.user_id,
                            );
                            void fetchUserWorkspaces(profile.user_id);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                              {getInitials(profile.full_name, profile.email)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            {profile.full_name && (
                              <p className="text-sm font-medium leading-none">
                                {profile.full_name}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {profile.email}
                            </p>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
              <span className="text-sm text-warning">
                All actions will be logged
              </span>
            </div>

            <Button
              className="w-full"
              variant="outline"
              disabled={!selectedUser || loadingAction === "impersonate"}
              onClick={() =>
                selectedUser && handleStartImpersonation(selectedUser)
              }
            >
              {loadingAction === "impersonate" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting session...
                </>
              ) : (
                "Start Session"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              {noUserWarning
                ? "Search and select a user above to enable actions"
                : `Acting on: ${selectedUser?.full_name ?? selectedUser?.email}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={noUserWarning || loadingAction === "reset"}
              onClick={handleResetPassword}
            >
              {loadingAction === "reset" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4" />
              )}
              Reset User Password
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={noUserWarning || loadingAction === "verify"}
              onClick={handleResendVerification}
            >
              {loadingAction === "verify" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              Resend Verification Email
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={
                noUserWarning ||
                userWorkspaces.length === 0 ||
                loadingAction === "export"
              }
              onClick={() => {
                setSelectedWorkspaceId(userWorkspaces[0]?.id ?? "");
                setExportDialogOpen(true);
              }}
            >
              {loadingAction === "export" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export Workspace Data
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start gap-2 text-destructive hover:text-destructive"
              disabled={
                noUserWarning ||
                userWorkspaces.length === 0 ||
                loadingAction === "suspend"
              }
              onClick={() => {
                setSelectedWorkspaceId(userWorkspaces[0]?.id ?? "");
                setSuspendDialogOpen(true);
              }}
            >
              {loadingAction === "suspend" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Ban className="w-4 h-4" />
              )}
              Suspend Workspace
            </Button>

            {/* User workspaces list (only when a user is selected and has workspaces) */}
            {selectedUser && userWorkspaces.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  Workspaces
                </p>
                {userWorkspaces.map((ws) => (
                  <div
                    key={ws.id}
                    className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors ${
                      selectedWorkspaceId === ws.id
                        ? "border-primary/50 bg-primary/5"
                        : "border-border hover:bg-accent/50"
                    }`}
                    onClick={() => setSelectedWorkspaceId(ws.id)}
                  >
                    <span className="font-medium truncate">{ws.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {ws.plan && (
                        <Badge variant="secondary" className="text-xs">
                          {ws.plan}
                        </Badge>
                      )}
                      <Badge
                        variant={
                          ws.status === "suspended" ? "destructive" : "outline"
                        }
                        className="text-xs"
                      >
                        {ws.status ?? "active"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Workspace Data</DialogTitle>
            <DialogDescription>
              This will download a JSON file containing workspace details,
              contacts, properties, and members.
            </DialogDescription>
          </DialogHeader>
          {userWorkspaces.length > 1 && (
            <div className="space-y-2 py-2">
              <p className="text-sm font-medium">Select workspace</p>
              {userWorkspaces.map((ws) => (
                <div
                  key={ws.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    selectedWorkspaceId === ws.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent/50"
                  }`}
                  onClick={() => setSelectedWorkspaceId(ws.id)}
                >
                  {selectedWorkspaceId === ws.id && (
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  )}
                  <span className="text-sm">{ws.name}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleExportData} disabled={!selectedWorkspaceId}>
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to suspend{" "}
              <span className="font-semibold text-foreground">
                {userWorkspaces.find((w) => w.id === selectedWorkspaceId)
                  ?.name ?? "this workspace"}
              </span>
              ? Members will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          {userWorkspaces.length > 1 && (
            <div className="space-y-2 py-2">
              <p className="text-sm font-medium">Select workspace</p>
              {userWorkspaces.map((ws) => (
                <div
                  key={ws.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    selectedWorkspaceId === ws.id
                      ? "border-destructive bg-destructive/5"
                      : "border-border hover:bg-accent/50"
                  }`}
                  onClick={() => setSelectedWorkspaceId(ws.id)}
                >
                  <span className="text-sm">{ws.name}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuspendDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspendWorkspace}
              disabled={!selectedWorkspaceId}
            >
              <Ban className="w-4 h-4 mr-2" />
              Suspend Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
